/**
 * Create Media Plan field-value helpers shared by Maestro's cards.
 *
 * Purpose: Pure helpers for reading and writing Create Media Plan form values
 *   from outside the form — matching a stored list value to its MDM option, and
 *   putting a value in the shape the form declares for its field. They used to
 *   live in ApprovalMappingField; ORCH-412 retired that control from the
 *   stepper (the stepper now renders the form's own FieldRenderers), so the
 *   helpers moved here rather than being tied to a component.
 *
 * Dependencies: none beyond the MDM value type — deliberately free of React.
 */

import type { MasterDataValue } from '../../types/mdm';

/**
 * Match a stored form value against MDM options by id, global id, then name.
 * MaestroMappingsStepper resolves user-picked values the same way the approval
 * dropdown does (ORCH-380).
 */
export function resolveMdmOption(
  stored: unknown,
  options: readonly MasterDataValue[]
): MasterDataValue | null {
  if (stored === undefined || stored === null || stored === '') return null;
  // Multiselect form values are arrays — pick the first element for the approval card dropdown.
  const scalar = Array.isArray(stored) ? stored[0] : stored;
  if (scalar === undefined || scalar === null || scalar === '') return null;
  const s = String(scalar).trim();
  if (!s) return null;
  for (const o of options) {
    if (String(o.id) === s) return o;
  }
  for (const o of options) {
    if (o.global_value_id && String(o.global_value_id) === s) return o;
  }
  const sl = s.toLowerCase();
  for (const o of options) {
    if (o.name.trim().toLowerCase() === sl) return o;
  }
  return null;
}

/** Multiselect list fields on the Create Media Plan form — values must be arrays of option ids.
 * Mirrors backend `CREATE_PLAN_MULTISELECT_LIST_FIELD_KEYS` (LLM-Service/app/tool_registry.py).
 * Wrapping keeps form state in the declared shape; the form and server also accept a scalar
 * (ORCH-354), so a field missing from this list still saves.
 */
const CREATE_PLAN_MULTISELECT_FD_KEYS = new Set<string>([
  'fd_product',
  'fd_advertiser',
  'fd_media-agency',
  'fd_medium-media-type',
]);

/**
 * Put a value in the shape the Create Media Plan form declares for that field.
 * The gate's Confirm handlers write the chosen value through this — since
 * ORCH-376 that write is the only path a Maestro proposal has into the plan.
 */
export function wrapForFieldShape(fdKey: string, value: unknown): unknown {
  if (!CREATE_PLAN_MULTISELECT_FD_KEYS.has(fdKey)) return value;
  if (value === null || value === undefined || value === '') return [];
  return Array.isArray(value) ? value : [value];
}
